import { cn } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { StatusChip } from '@/components/ui/status-chip';
import {
  CLIENT_TYPE_CHIP_VARIANTS,
  CLIENT_TYPE_LABELS,
  formatTypology,
  getRiskScoreClass,
  notInSlackReason,
  STATUS_CHIP_VARIANTS,
  STATUS_LABELS,
} from '@/features/market-surveillance/surveillance-meta';
import type { SurveillanceAlertRow } from '@/services/api/market-surveillance';

// Sorting is disabled everywhere: the list is cursor-paginated and server-sorted —
// client sorting would only reorder the visible page and mislead.
export const ALERT_TABLE_COLUMNS: ColumnDef<SurveillanceAlertRow, unknown>[] = [
  {
    accessorKey: 'alertId',
    header: 'Alert ID',
    enableSorting: false,
    cell: ({ row }) => <UuidCell value={row.original.alertId} />,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    enableSorting: false,
    cell: ({ row }) => <DateCell value={row.original.createdAt} />,
  },
  {
    accessorKey: 'typology',
    header: 'Type',
    enableSorting: false,
    // Only the "All alerts" scope returns rows narc kept out of Slack; flag them so they read as context, not queue
    cell: ({ row }) => (
      <span className="flex items-center gap-2 whitespace-nowrap">
        {formatTypology(row.original.typology)}
        {row.original.actionable ? null : (
          <span className="rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground" title={notInSlackReason(row.original.suppressedBy)}>
            Not in Slack
          </span>
        )}
      </span>
    ),
  },
  {
    accessorKey: 'riskScore',
    header: 'Risk Score',
    enableSorting: false,
    cell: ({ row }) => <span className={cn('tabular-nums font-medium', getRiskScoreClass(row.original.riskScore))}>{row.original.riskScore}</span>,
  },
  {
    accessorKey: 'pid',
    header: 'PID',
    enableSorting: false,
    cell: ({ row }) => <UuidCell value={row.original.pid} />,
  },
  {
    accessorKey: 'clientType',
    header: 'Client Type',
    enableSorting: false,
    cell: ({ row }) => (
      <StatusChip className="whitespace-nowrap px-2 py-0.5 text-xs" variant={CLIENT_TYPE_CHIP_VARIANTS[row.original.clientType]}>
        {CLIENT_TYPE_LABELS[row.original.clientType]}
      </StatusChip>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => (
      <StatusChip className="px-2 py-0.5 text-xs" variant={STATUS_CHIP_VARIANTS[row.original.status]}>
        {STATUS_LABELS[row.original.status]}
      </StatusChip>
    ),
  },
];
